import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Spinner } from '@/components/ui/Spinner'
import { FileIcon, Plus, Download, Trash2 } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { useDeleteClientDocument } from '@/hooks/useClients'
import type { Document } from '@/types/client'

interface ClientDocumentsTabProps {
  documents?: Document[]
  clientId: string
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function getFileIcon(type: string): string {
  if (type.includes('pdf')) return 'pdf'
  if (type.includes('image')) return 'image'
  if (type.includes('word') || type.includes('document')) return 'doc'
  if (type.includes('excel') || type.includes('spreadsheet')) return 'xls'
  return 'file'
}

export function ClientDocumentsTab({ documents, clientId }: ClientDocumentsTabProps) {
  const deleteMutation = useDeleteClientDocument()

  const handleDelete = async (documentId: string) => {
    if (confirm('Voulez-vous vraiment supprimer ce document ?')) {
      await deleteMutation.mutateAsync({ documentId, clientId })
    }
  }

  if (!documents || documents.length === 0) {
    return (
      <EmptyState
        icon={<FileIcon className="w-12 h-12" />}
        title="Aucun document"
        description="Ce client n'a pas encore de document partage."
        action={
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            Ajouter un document
          </Button>
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" leftIcon={<Plus className="w-4 h-4" />}>
          Ajouter un document
        </Button>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-beige-50 border-b border-beige-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Nom
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Type
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Taille
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Ajoute le
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-beige-200">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-beige-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileIcon className="w-5 h-5 text-brown-400" />
                      <span className="font-medium text-brown-900">{doc.nom}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-600 uppercase">
                    {getFileIcon(doc.type)}
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-500">
                    {formatFileSize(doc.taille)}
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-500">
                    {formatDate(doc.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <a href={doc.url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(doc.id)}
                        disabled={deleteMutation.isPending}
                        title="Supprimer"
                      >
                        {deleteMutation.isPending ? (
                          <Spinner size="sm" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-red-500" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
